import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ButtonDS,
  NotInterestedOutlined,
  SidePanelDS,
  Spacer,
  TableTagBadge,
  Tag,
  triggerToast,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import {
  badgesSlice,
  selectBadgesList,
  selectBadgeUpdateStatus,
  selectNewBadgeStatus,
  selectReplacementBadgeStatus,
} from "../badgesSlice";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { StepsToInOppositionBadge } from "../../modals/badges/StepsToInOppositionModal";
import "../../../../../i18n/i18n";
import { InformationsBadge } from "./informationsBadgeSection";
import { StatementsSummarySectionBadge } from "./statementsSummarySectionBadge";

type BadgeSidePanelProps = {
  canDeactivate?: boolean;
  lockBadgeAction: (isBadgeLocked: boolean, badgeUuid: string) => void;
};

export const BadgeSidePanel: FunctionComponent<BadgeSidePanelProps> = (
  props: BadgeSidePanelProps,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const firstPath = location.pathname.split("/")[1]; // home or departments

  const params = useParams();
  const updateBadgeStatus = useAppSelector(selectBadgeUpdateStatus);
  const badgesList = useAppSelector(selectBadgesList);
  const badgeNewStatus = useAppSelector(selectNewBadgeStatus);
  const badge = badgesList.find((c) => c.uuid === params.id);
  const [areStepsToInOppositionVisible, setAreStepsToInOppositionVisible] =
    useState(false);
  const replacementBadgeStatus = useAppSelector(selectReplacementBadgeStatus);

  const isBadgeLocked = useMemo(() => {
    return badge?.status === "LOCKED";
  }, [badge?.status]);

  const isBadgeInOpposition = useMemo(() => {
    return badge?.status === "IN_OPPOSITION" || badge?.status === "ERROR";
  }, [badge?.status]);

  const isBadgeToActivate = useMemo(() => {
    return badge?.status === "ORDERED";
  }, [badge?.status]);

  useEffect(() => {
    if (!badge) {
      navigate(`/${firstPath}/cards/badges`);
    }
  }, [navigate, badge, firstPath]);

  useEffect(() => {
    if (updateBadgeStatus === "success") {
      triggerToast(
        t(`badges.updatedStatus.${badgeNewStatus}.success`) || "",
        "valid",
      );
      dispatch(badgesSlice.actions.resetBadgeUpdateStatus());
    } else if (updateBadgeStatus === "failed") {
      triggerToast(t("badges.updatedStatus.failure") || "", "error");
      dispatch(badgesSlice.actions.resetBadgeUpdateStatus());
    }
  }, [updateBadgeStatus, t, badgeNewStatus, dispatch]);

  useEffect(() => {
    if (replacementBadgeStatus === "success") {
      triggerToast(t(`badges.replacement.success`) || "", "valid");
      dispatch(badgesSlice.actions.resetReplacementBadgeStatus());
    } else if (replacementBadgeStatus === "failed") {
      triggerToast(t("badges.replacement.failure") || "", "error");
      dispatch(badgesSlice.actions.resetReplacementBadgeStatus());
    }
  }, [replacementBadgeStatus, t, dispatch]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/cards/badges`);
  }, [navigate, firstPath]);

  if (!badge) {
    return;
  }
  return (
    <SidePanelDS.SidePanel
      visible={!!badge}
      onClose={() => !areStepsToInOppositionVisible && onSidePanelClosed()}
      absoluteSection={
        props.canDeactivate ? (
          <ButtonDS
            leftIcon={
              <StyledIconColor $isInOpposition={isBadgeInOpposition}>
                <NotInterestedOutlined />
              </StyledIconColor>
            }
            text={t("badges.sidePanel.button.opposition")}
            format={"fill"}
            buttonType={"primary"}
            onClick={() =>
              badge.status !== "IN_OPPOSITION" &&
              setAreStepsToInOppositionVisible(true)
            }
            iconColor={"errorNormal"}
            disabled={isBadgeInOpposition}
          />
        ) : undefined
      }
    >
      <SidePanelDS.Section
        verticalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          <Tag
            text={t("badges.sidePanel.title")}
            borderColor={colors["colors/borders/cells/cells"]}
            backgroundColor={colors["colors/text/white"]}
          />
          <Spacer y={1.5} />
          <TableTagBadge status={t(`badges.status.${badge.status}`)} />
          <Spacer y={1} />
          <InformationsBadge
            firstPath={firstPath}
            isBadgeLocked={isBadgeLocked}
            isBadgeToActivate={isBadgeToActivate}
            isBadgeInOpposition={isBadgeInOpposition}
            lockBadge={(isBadgeLocked: boolean, badgeUuid: string) =>
              props.lockBadgeAction(isBadgeLocked, badgeUuid)
            }
            vehicle={{
              name: badge.registrationNumber,
              uuid: badge.vehicleUuid,
              reference: badge.reference,
            }}
            badgeUuid={badge.uuid}
          />
        </StyledPanelContainer>
      </SidePanelDS.Section>
      <StatementsSummarySectionBadge firstPath={firstPath} />
      <StepsToInOppositionBadge
        isVisible={areStepsToInOppositionVisible}
        onCloseModal={() => {
          setAreStepsToInOppositionVisible(false);
        }}
        badge={{ uuid: badge.uuid, number: badge.number }}
        vehicle={{
          registrationNumber: badge.registrationNumber,
          reference: badge.reference,
          uuid: badge.vehicleUuid,
        }}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledIconColor = styled.div<{ $isInOpposition: boolean }>`
  & svg {
    fill: ${({ $isInOpposition }) => ($isInOpposition ? "grey" : "")};
  }
`;
